import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CircleNotch, ShieldCheck, Lightning, TrendUp, TrendDown, Equals, CalendarBlank } from "@phosphor-icons/react";
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL;

const strengthColors = {
  "VERY HIGH": "bg-red-600 text-white",
  HIGH: "bg-orange-500 text-white",
  MEDIUM: "bg-amber-500 text-white",
  LOW: "bg-gray-400 text-white",
};

const posColor = {
  VALLEY: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", badge: "bg-amber-100 text-amber-700" },
  PEAK: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", badge: "bg-emerald-100 text-emerald-700" },
  MID: { bg: "bg-gray-50", border: "border-gray-300", text: "text-gray-600", badge: "bg-gray-100 text-gray-600" },
};

export default function SignalsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [connData, setConnData] = useState(null);
  const [dateSignal, setDateSignal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/analysis/signals`).then(r => setData(r.data)).catch(() => {}),
      axios.get(`${API}/api/analysis/mirror-complement`).then(r => setConnData(r.data)).catch(() => {}),
      axios.get(`${API}/api/analysis/date-pattern-signal`).then(r => setDateSignal(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="signals-loading">
      <CircleNotch size={40} className="animate-spin text-blue-600" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500">Failed to load signals</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="signals-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-900" data-testid="back-button">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">W58: Signals</h1>
            <p className="text-xs text-gray-500">Hard Principles (combo filters) + Live Oscillation Signals for adjacent ball gaps</p>
          </div>
          <div className="text-right text-[10px] text-gray-400">
            <p>Last draw: #{data.last_draw?.draw_number} ({data.last_draw?.draw_date})</p>
            <p>{data.cy_draws} draws in {data.current_year} | {data.total_draws} total</p>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6 space-y-6">


        <WindowNarrative windowId="W58" {...NARRATIVES.W58} className="mb-5" />
        {/* Date Pattern Signal — Next Draw Dates */}
        {dateSignal?.date_signals?.length > 0 && (
          <div className="space-y-4" data-testid="date-pattern-signal">
            {dateSignal.date_signals.map((sig, si) => {
              const veryHotNumbers = (sig.hot_numbers || []).filter(h => h.date_pct >= 30);
              const warmNumbers = (sig.hot_numbers || []).filter(h => h.date_pct >= 20 && h.date_pct < 30);
              return (
              <div key={si} className={`bg-white rounded-lg overflow-hidden ${sig.strength === 'VERY HIGH' ? 'border-2 border-red-400' : sig.strength === 'HIGH' ? 'border-2 border-orange-400' : 'border border-gray-200'}`}>
                <div className={`p-4 border-b ${sig.strength === 'VERY HIGH' ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white border-red-400' : sig.strength === 'HIGH' ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white border-orange-400' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <CalendarBlank size={22} weight="fill" className={sig.strength === 'VERY HIGH' || sig.strength === 'HIGH' ? 'text-white' : 'text-blue-600'} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className={`font-bold text-lg ${sig.strength === 'VERY HIGH' || sig.strength === 'HIGH' ? 'text-white' : 'text-gray-900'}`}>
                          Date Pattern Signal — {sig.target_date.date_str} ({sig.target_date.weekday})
                        </h2>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black ${strengthColors[sig.strength] || 'bg-gray-400 text-white'}`}>{sig.strength}</span>
                      </div>
                      <p className={`text-xs mt-0.5 ${sig.strength === 'VERY HIGH' || sig.strength === 'HIGH' ? 'text-white/70' : 'text-gray-500'}`}>
                        {sig.exact_match_draws} historical draws on this exact date | {sig.month_draws} draws in same month
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  {/* Hot Numbers */}
                  {sig.hot_numbers?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-gray-800 mb-2">Recurring Numbers on {sig.target_date.day}/{sig.target_date.month}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {veryHotNumbers.map(h => (
                          <div key={h.number} className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2 ${h.date_pct >= 50 ? 'border-red-400 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                            <span className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-bold text-lg text-white ${h.date_pct >= 50 ? 'bg-red-600' : 'bg-amber-500'}`}>{h.number}</span>
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{h.date_count}/{h.date_appearances}</p>
                              <p className="text-[10px] text-gray-500">{h.date_pct}% on date</p>
                              <p className="text-[10px] text-gray-400">Baseline: {h.baseline_pct}%</p>
                              <p className={`text-[10px] font-bold ${h.lift > 20 ? 'text-red-600' : h.lift > 10 ? 'text-amber-600' : 'text-gray-500'}`}>+{h.lift}% lift</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Numbers with 20% (1/5) appearance */}
                      {warmNumbers.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Also appeared ({warmNumbers.length} numbers)</p>
                          <div className="flex gap-1 flex-wrap">
                            {warmNumbers.map(h => (
                              <span key={h.number} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded px-2 py-0.5 text-[10px] font-mono font-bold">
                                {h.number} <span className="text-gray-400">({h.date_count}x, +{h.lift}%)</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Recurring Consecutive Pairs */}
                    {sig.recurring_pairs?.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <h4 className="text-xs font-bold text-red-800 mb-2">Recurring Consecutive Pairs</h4>
                        {sig.recurring_pairs.map((p, pi) => (
                          <div key={pi} className="flex items-center gap-2 mb-1">
                            <span className="inline-flex w-7 h-7 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold text-xs">{p.pair[0]}</span>
                            <span className="inline-flex w-7 h-7 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold text-xs">{p.pair[1]}</span>
                            <span className="text-xs font-bold text-red-700">{p.count}x ({p.pct}%)</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Hot Lucky Stars */}
                    {sig.hot_lucky_stars?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <h4 className="text-xs font-bold text-amber-800 mb-2">Lucky Stars on this Date</h4>
                        <div className="flex gap-2 flex-wrap">
                          {sig.hot_lucky_stars.map(ls => (
                            <div key={ls.number} className="flex items-center gap-1.5">
                              <span className="inline-flex w-7 h-7 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-xs">{ls.number}</span>
                              <span className="text-[10px] font-bold text-amber-700">{ls.date_count}x ({ls.date_pct}%)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Historical Draws */}
                    {sig.historical_draws?.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <h4 className="text-xs font-bold text-gray-600 mb-2">Historical Draws on {sig.target_date.day}/{sig.target_date.month}</h4>
                        <div className="space-y-1.5">
                          {sig.historical_draws.map(hd => (
                            <div key={hd.year} className="flex items-center gap-2 text-[10px]">
                              <span className="font-bold text-gray-500 w-10">{hd.year}</span>
                              <div className="flex gap-0.5">
                                {hd.balls.map((b, bi) => {
                                  const isHot = sig.hot_numbers.some(h => h.number === b && h.date_pct >= 40);
                                  return (
                                    <span key={bi} className={`inline-flex w-6 h-6 rounded-full items-center justify-center font-mono font-bold text-[10px] ${isHot ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{b}</span>
                                  );
                                })}
                              </div>
                              <span className="text-gray-400">|</span>
                              <div className="flex gap-0.5">
                                {hd.lucky_stars.map((ls, li) => (
                                  <span key={li} className="inline-flex w-5 h-5 rounded-full bg-amber-100 text-amber-700 items-center justify-center font-mono font-bold text-[9px]">{ls}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        )}

        {/* Hard Principles */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="principles-section">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-rose-50">
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} weight="fill" className="text-red-600" />
              <h2 className="font-bold text-gray-900">Hard Principles — Applied to ALL Combo Generators</h2>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Adjacent ball gap caps based on P90 historical coverage. Combos with 2+ violations are eliminated. Max 1 wide gap allowed (99.3% historical pass rate).</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200">
            {data.principles?.map((p) => (
              <div key={p.id} className="bg-white p-4 text-center">
                <p className="text-[10px] text-red-600 font-bold uppercase">{p.id}</p>
                <p className="text-xl font-black text-gray-900 mt-1">{p.name}</p>
                <p className="text-xs text-gray-400 mt-1">{p.coverage} coverage</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{p.applied}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Oscillation Signals — Per Pair */}
        {data.signals?.map((sig) => {
          const pc = posColor[sig.position] || posColor.MID;
          const trend = sig.trend_2026 || [];
          const tMin = Math.min(...trend, 0);
          const tMax = Math.max(...trend, 1);
          const tRange = tMax - tMin || 1;

          return (
            <div key={sig.key} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`signal-${sig.key}`}>
              <div className={`px-5 py-3 border-b ${pc.border} ${pc.bg} flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <Lightning size={20} weight="fill" className={pc.text} />
                  <div>
                    <h3 className={`font-bold text-lg ${pc.text}`}>{sig.label}</h3>
                    <p className="text-[10px] text-gray-500">Cap: {sig.label} &lt; {sig.cap} ({sig.cap_coverage}% coverage)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-black text-gray-900">{sig.last_value}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${pc.badge}`}>
                    {sig.position === "VALLEY" && "AT VALLEY"}
                    {sig.position === "PEAK" && "AT PEAK"}
                    {sig.position === "MID" && "MID-RANGE"}
                  </span>
                </div>
              </div>

              <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Mini trend chart */}
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Last {trend.length} Draws</p>
                  <div className="relative h-20 bg-gray-50 rounded-lg p-2">
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      {/* Hist avg line */}
                      {sig.hist_avg > tMin && sig.hist_avg < tMax && (
                        <line x1="0" x2="100"
                          y1={100 - ((sig.hist_avg - tMin) / tRange) * 100}
                          y2={100 - ((sig.hist_avg - tMin) / tRange) * 100}
                          stroke="#ef4444" strokeWidth="0.5" strokeDasharray="3,3" vectorEffect="non-scaling-stroke" />
                      )}
                      <polyline
                        points={trend.map((v, i) => `${(i / Math.max(trend.length - 1, 1)) * 100},${100 - ((v - tMin) / tRange) * 100}`).join(" ")}
                        fill="none" stroke="#3b82f6" strokeWidth="1.5" vectorEffect="non-scaling-stroke"
                      />
                      {trend.map((v, i) => (
                        <circle key={i}
                          cx={(i / Math.max(trend.length - 1, 1)) * 100}
                          cy={100 - ((v - tMin) / tRange) * 100}
                          r="2" fill={i === trend.length - 1 ? "#ef4444" : "#3b82f6"}
                        />
                      ))}
                    </svg>
                  </div>
                  <div className="flex gap-1 mt-2 justify-center">
                    {trend.slice(-5).map((v, i) => (
                      <span key={i} className="font-mono text-xs font-bold bg-gray-100 px-1.5 py-0.5 rounded">{v}</span>
                    ))}
                  </div>
                </div>

                {/* Averages & Cycles */}
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Average Comparison</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-400">{data.current_year}:</span>{" "}
                        <span className={`font-bold ${sig.avg_gap > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{sig.cy_avg}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Historical:</span>{" "}
                        <span className="font-bold text-gray-700">{sig.hist_avg}</span>
                      </div>
                    </div>
                    <div className={`mt-1 text-[10px] font-bold ${sig.avg_gap < -0.5 ? 'text-red-600' : sig.avg_gap > 0.5 ? 'text-emerald-600' : 'text-gray-500'}`}>
                      Gap: {sig.avg_gap > 0 ? "+" : ""}{sig.avg_gap}
                      {sig.avg_gap < -0.5 && " — Higher diff likely"}
                      {sig.avg_gap > 0.5 && " — Running above avg"}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">V-to-P Cycles ({data.current_year})</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-gray-400">Actual:</span> <span className="font-bold">{sig.cycles_2026}</span></div>
                      <div><span className="text-gray-400">Expected:</span> <span className="font-bold">{sig.expected_cycles}</span></div>
                      <div><span className="text-gray-400">Recovery:</span> <span className="font-bold">{sig.avg_recovery_draws} draws</span></div>
                      <div><span className="text-gray-400">Avg Jump:</span> <span className="font-bold">+{sig.avg_recovery_jump}</span></div>
                    </div>
                    <div className={`mt-1 text-[10px] font-bold ${sig.cycles_2026 < sig.expected_cycles ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {sig.cycles_2026 < sig.expected_cycles ? `Running ${sig.expected_cycles - sig.cycles_2026} cycles behind pace` : "On pace"}
                    </div>
                  </div>
                </div>

                {/* Oscillation Signals */}
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Oscillation Signal</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <TrendDown size={14} className="text-red-500 mt-0.5 shrink-0" />
                        <div className="text-xs">
                          <span className="text-gray-600">After HIGH (≥15):</span>{" "}
                          <span className="font-bold">{sig.oscillation.after_high_drop_pct}%</span>
                          <span className="text-gray-400"> next ≤8</span>
                          <span className="text-[10px] text-gray-400 ml-1">({sig.oscillation.after_high_tests} tests)</span>
                          <span className={`ml-1 text-[10px] font-bold ${sig.oscillation.high_drop_edge > 5 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            +{sig.oscillation.high_drop_edge}% edge
                          </span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <TrendUp size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                        <div className="text-xs">
                          <span className="text-gray-600">After LOW (≤3):</span>{" "}
                          <span className="font-bold">{sig.oscillation.after_low_bounce_pct}%</span>
                          <span className="text-gray-400"> next in 5-15</span>
                          <span className="text-[10px] text-gray-400 ml-1">({sig.oscillation.after_low_tests} tests)</span>
                          <span className={`ml-1 text-[10px] font-bold ${sig.oscillation.low_bounce_edge > 3 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {sig.oscillation.low_bounce_edge > 0 ? "+" : ""}{sig.oscillation.low_bounce_edge}% edge
                          </span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Equals size={14} className="text-gray-400 mt-0.5 shrink-0" />
                        <div className="text-xs text-gray-400">
                          Baseline (any draw → 5-15): {sig.oscillation.baseline_mid_pct}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Current Signal */}
                  {sig.position === "VALLEY" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`signal-active-${sig.key}`}>
                      <p className="text-xs font-bold text-amber-800">
                        {sig.label} = {sig.last_value} (at valley)
                      </p>
                      <p className="text-[10px] text-amber-700 mt-0.5">
                        Expect bounce to {Math.round(sig.last_value + sig.avg_recovery_jump)}-range in ~{sig.avg_recovery_draws} draws
                      </p>
                    </div>
                  )}
                  {sig.position === "PEAK" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid={`signal-active-${sig.key}`}>
                      <p className="text-xs font-bold text-emerald-800">
                        {sig.label} = {sig.last_value} (at peak)
                      </p>
                      <p className="text-[10px] text-emerald-700 mt-0.5">
                        Expect drop toward lower values in the next 1-2 draws
                      </p>
                    </div>
                  )}
                  {sig.last_value >= 15 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-xs font-bold text-red-800">
                        HIGH VALUE ({sig.last_value} ≥ 15) — {sig.oscillation.after_high_drop_pct}% chance next ≤ 8
                      </p>
                    </div>
                  )}
                  {sig.last_value <= 3 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-bold text-blue-800">
                        LOW VALUE ({sig.last_value} ≤ 3) — {sig.oscillation.after_low_bounce_pct}% chance next in 5-15
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Missing Digit Signal */}
        {data.digit_signals && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="digit-signal">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-rose-50 to-pink-50">
              <h2 className="font-bold text-gray-900">Missing Digit Signal — {data.current_year} vs Historical</h2>
              <p className="text-[10px] text-gray-400">Digits (0-9) that are MISSING or LACKING at each ball position in {data.current_year}. Auto-updates with each draw.</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {["ball_1","ball_2","ball_3","ball_4","ball_5"].map((bk, bi) => {
                  const ds = data.digit_signals?.[bk];
                  if (!ds) return null;
                  const hasMissing = ds.missing?.length > 0;
                  const hasLacking = ds.lacking?.length > 0;
                  return (
                    <div key={bk} className={`rounded-lg border p-3 ${hasMissing ? 'border-red-300 bg-red-50' : hasLacking ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                      <p className="text-xs font-bold text-gray-800 mb-2">Ball {bi + 1}</p>
                      {ds.missing?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-[10px] font-bold text-red-600 uppercase mb-1">Missing</p>
                          <div className="flex gap-1 flex-wrap">
                            {ds.missing.map(m => (
                              <span key={m.digit} className="inline-flex w-7 h-7 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold text-xs" title={`Hist: ${m.hist_pct}%`}>{m.digit}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {ds.lacking?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-[10px] font-bold text-amber-600 uppercase mb-1">Lacking</p>
                          <div className="flex gap-1 flex-wrap">
                            {ds.lacking.map(m => (
                              <span key={m.digit} className="inline-flex w-7 h-7 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-xs" title={`2026: ${m.cy_pct}% vs Hist: ${m.hist_pct}% (${m.diff}%)`}>{m.digit}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {ds.surplus?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Surplus</p>
                          <div className="flex gap-1 flex-wrap">
                            {ds.surplus.map(m => (
                              <span key={m.digit} className="inline-flex w-7 h-7 rounded-full bg-emerald-500 text-white items-center justify-center font-mono font-bold text-xs" title={`2026: ${m.cy_pct}% vs Hist: ${m.hist_pct}% (+${m.diff}%)`}>{m.digit}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {!hasMissing && !hasLacking && ds.surplus?.length === 0 && (
                        <p className="text-[10px] text-gray-400">All digits normal</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Number Connections Signal */}
        {connData?.connections?.active_signals && Object.keys(connData.connections.active_signals).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="connection-signal">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
              <h2 className="font-bold text-gray-900">Number Connection Signal — {connData.current_year}</h2>
              <p className="text-[10px] text-gray-400">Last draw [{connData.last_draw?.balls?.join(', ')}] — partners most likely to follow based on {connData.current_year} co-occurrence patterns</p>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
              {(connData.last_draw?.balls || []).map(num => {
                const partners = connData.connections.active_signals?.[num] || [];
                return (
                  <div key={num} className={`rounded-lg border p-3 ${partners.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="text-center mb-2">
                      <span className="inline-flex w-9 h-9 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-lg">{num}</span>
                    </div>
                    {partners.length > 0 ? (
                      <div className="flex gap-1 flex-wrap justify-center">
                        {partners.slice(0, 5).map(p => (
                          <span key={p.number} className="text-[10px] font-mono font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            {p.number}({p.count}x)
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-gray-400 text-center">No strong links</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Consecutive Pair Shift Signal — VERY HIGH */}
        {data.pair_shift_signal && (
          <div className="bg-white border-2 border-red-400 rounded-lg overflow-hidden" data-testid="pair-shift-signal">
            <div className="p-4 border-b border-red-300 bg-gradient-to-r from-red-600 to-rose-600 text-white">
              <div className="flex items-center gap-2">
                <Lightning size={24} weight="fill" />
                <div>
                  <h2 className="font-bold text-xl">Consecutive Pair Shift — VERY HIGH SIGNAL</h2>
                  <p className="text-xs text-white/80">Pattern: When pair A shifts by X to pair B, the same shift X applies to the next response pair</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Shift Chain */}
              {data.pair_shift_signal.shifts?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Recent Pair Shift Chain</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {data.pair_shift_signal.shifts.map((s, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <span className="inline-flex items-center gap-0.5 bg-indigo-100 text-indigo-800 rounded-full px-2.5 py-1 text-xs font-bold font-mono">
                          {s.from_pair[0]},{s.from_pair[1]}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.shift > 0 ? 'bg-emerald-100 text-emerald-700' : s.shift < 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100'}`}>
                          {s.shift > 0 ? '+' : ''}{s.shift}
                        </span>
                        {i === data.pair_shift_signal.shifts.length - 1 && (
                          <span className="inline-flex items-center gap-0.5 bg-indigo-100 text-indigo-800 rounded-full px-2.5 py-1 text-xs font-bold font-mono">
                            {s.to_pair[0]},{s.to_pair[1]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Predictions */}
              {data.pair_shift_signal.predictions?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Predicted Next Pairs</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.pair_shift_signal.predictions.map((pred, i) => (
                      <div key={i} className={`rounded-lg border-2 p-4 ${pred.decade_missing ? 'border-red-400 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex gap-1">
                            <span className="inline-flex w-10 h-10 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold text-lg">{pred.predicted_pair[0]}</span>
                            <span className="inline-flex w-10 h-10 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold text-lg">{pred.predicted_pair[1]}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-[10px] font-bold mt-0.5">
                              Decade {pred.predicted_decade}
                              {pred.decade_missing && <span className="ml-1 text-red-700 bg-red-100 px-1.5 py-0.5 rounded">MISSING DECADE — OVERDUE</span>}
                            </p>
                            <p className="text-[10px] text-gray-500">{pred.proof_paths?.length || 0} proof path(s)</p>
                          </div>
                        </div>
                        {/* All proof paths for this prediction */}
                        <div className="space-y-1.5 border-t border-red-200 pt-2">
                          {(pred.proof_paths || []).map((pp, pi) => (
                            <div key={pi} className={`text-[11px] flex items-start gap-1.5 ${pi === 0 ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                              <span className={`shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${pp.cross_pair ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{pi + 1}</span>
                              <div>
                                <span className="font-mono">{pp.from_pair[0]},{pp.from_pair[1]}</span>
                                <span className={`mx-1 px-1 py-0.5 rounded text-[10px] font-bold ${pp.shift < 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {pp.shift > 0 ? '+' : ''}{pp.shift}
                                </span>
                                <span className="font-mono">= {pred.predicted_pair[0]},{pred.predicted_pair[1]}</span>
                                {pp.proven_by && <span className="ml-1 text-[9px] text-gray-400">proven: {pp.proven_by}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Decades */}
              {data.pair_shift_signal.missing_decades?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-red-800">
                    Decades with ZERO consecutive pairs in {data.current_year}: {data.pair_shift_signal.missing_decades.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Methodology */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-5">
          <h3 className="font-bold text-indigo-900 mb-2">Methodology</h3>
          <div className="space-y-1.5 text-xs text-indigo-800">
            <p><strong>Hard Principles:</strong> Adjacent ball gap caps (B2-B1 &lt; 19, B3-B2 &lt; 19, B4-B3 &lt; 20, B5-B4 &lt; 19) eliminate ~10% of combos that historically almost never occur. Applied as strict filters across all combo generators.</p>
            <p><strong>Oscillation Signals:</strong> Observational only — not automated as weights. Shows whether the last draw's gap is at a peak/valley and the historical probability of reversal. Use manually when reviewing generated combos.</p>
            <p><strong>Valley Recovery:</strong> Historical avg of 1 cycle every 3.2 draws. When gap is at valley, expect a bounce of +10.9 on average within ~1.6 draws.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
